import {css} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {FiltersIcon} from "@pg-design/icons-module";
import {BrandPreferencesIcon} from "@pg-design/icons-module";
import {Image} from "@pg-design/image-module";
import {Text} from "@pg-design/text-module";
import {kmAppLink} from "@pg-mono/km-routes";

import {useResponsiveLinkTarget} from "../../../hooks/use_responsive_link_target";
import {KitchenType} from "../../../property_configurator/types/KitchenType";
import {useAppSelector} from "../../../store/hooks";
import {useIsAuthenticated} from "../../../user/hooks/use_is_authenticated";
import {getFullKMLink} from "../../../utils/get_full_km_link";
import {useForYouModalClose} from "../../hooks/use_for_you_modal_close";
import {ForYouAdditionalAreaType} from "../../types/IForYouAdditional";
import {IForYouModal} from "../../types/IForYouModal";
import {ForYouModalButton} from "./ForYouModalButton";

const consultantImg = require("../../images/for-you-consultant.png");

const kmPreferencesLink = getFullKMLink(kmAppLink.site.user.preferencesWithParams);

export const ForYouModalSummary = () => {
    const isAuthenticated = useIsAuthenticated();
    const modalState = useAppSelector((state) => state.forYouModal);

    const closeModal = useForYouModalClose();
    const target = useResponsiveLinkTarget();

    return (
        <div className={summaryContent}>
            {isAuthenticated && (
                <SummaryBox
                    image={
                        <div className={brandIconWrap}>
                            <BrandPreferencesIcon wrapperSize="12.4" wrapperColor="transparent" size="12.4" />
                        </div>
                    }
                    title="Twoje kryteria"
                    text={convertToSummaryText(modalState)}
                    button={
                        <Button iconLeft={FiltersIcon} href={kmPreferencesLink} className={button} variant="filled_primary" target={target}>
                            Zarządzaj
                        </Button>
                    }
                />
            )}
            <SummaryBox
                image={<Image src={consultantImg} width="124px" height="124px" alt="zdjęcie konsutanta" />}
                title="Anna Kowalska"
                text={
                    <>
                        Twój doradca nieruchomości
                        <br />
                        Godziny pracy: <br />
                        pon-pt: 9:00-18:00
                    </>
                }
                button={<ForYouModalButton onClick={closeModal} buttonText="Zakończ" className={buttonWrap} buttonClassName={button} />}
            />
        </div>
    );
};

function convertToSummaryText(modalState: IForYouModal) {
    const hasRooms = Boolean(modalState.criteria?.rooms.lower);

    const areaText = `${!hasRooms ? "Mieszkanie " : ""}${modalState.criteria?.area.lower}-${modalState.criteria?.area.upper} m2`;
    const budgetText = `do ${modalState.criteria?.budget} zł`;
    const locationText = `w lokalizacji ${modalState.mainRegionName}`;

    const textParts = [areaText, budgetText, locationText];

    if (hasRooms) {
        const singleRoom = modalState.criteria?.rooms.lower === modalState.criteria?.rooms.upper;
        const roomsText = singleRoom ? modalState.criteria?.rooms.lower : `${modalState.criteria?.rooms.lower}-${modalState.criteria?.rooms.upper}`;

        const roomText = `Mieszkanie ${roomsText} pokojowe`;
        textParts.splice(0, 0, roomText);
    }

    const additionalMap = new Map<ForYouAdditionalAreaType, string>([
        [ForYouAdditionalAreaType.BALCONY, "balkonem"],
        [ForYouAdditionalAreaType.LOGGIA, "loggią"],
        [ForYouAdditionalAreaType.TERRACE, "tarasem"],
        [ForYouAdditionalAreaType.GARDEN, "ogródkiem"]
    ]);

    const kitchenMap = new Map<KitchenType, string>([
        [KitchenType.KITCHENETTE, "aneksem kuchennym"],
        [KitchenType.CLOSED, "oddzielną kuchnią"]
    ]);

    const hasAdditionalAndKitchent = Boolean(modalState.additional?.additional_areas && modalState.additional?.kitchen);
    if (hasAdditionalAndKitchent) {
        const additionalText = additionalMap.get(modalState.additional?.additional_areas as unknown as ForYouAdditionalAreaType);
        const kitchenText = kitchenMap.get(modalState.additional?.kitchen as KitchenType);
        const additionalAndKitchenText = `z ${kitchenText} i ${additionalText}`;

        textParts.splice(textParts.length - 1, 0, additionalAndKitchenText);
    }

    const hasAdditionalButNoKitchen = Boolean(modalState.additional?.additional_areas && !modalState.additional?.kitchen);
    if (hasAdditionalButNoKitchen) {
        const additionalText = additionalMap.get(modalState.additional?.additional_areas as unknown as ForYouAdditionalAreaType);
        const additionalTextFull = `z ${additionalText}`;
        textParts.splice(textParts.length - 1, 0, additionalTextFull);
    }

    const hasKitchenButNoAdditional = Boolean(modalState.additional?.kitchen && !modalState.additional?.additional_areas);
    if (hasKitchenButNoAdditional) {
        const kitchenText = kitchenMap.get(modalState.additional?.kitchen as KitchenType);
        const kitchenTextFull = `z ${kitchenText}`;
        textParts.splice(textParts.length - 1, 0, kitchenTextFull);
    }

    return textParts.join(", ");
}
interface SummaryBoxProps {
    image: React.ReactNode;
    title: string;
    text: string | React.ReactNode;
    button: React.ReactNode;
}

function SummaryBox(props: SummaryBoxProps) {
    const {image, title, text, button} = props;

    return (
        <div className={summaryBoxStyle}>
            {image}
            <div className={contentStyle}>
                <Text variant="headline_5" as="p" align="center">
                    {title}
                </Text>
                <Text variant="info_txt_1" align="center" color="var(--colors-gray-700)">
                    {text}
                </Text>
            </div>
            {button}
        </div>
    );
}

const summaryContent = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    backgroundColor: "var(--colors-gray-100)",

    pt: 1.5,
    pr: 1.5,
    pb: 1.5,
    pl: 1.5,
    width: "100%",
    height: "100%",
    gap: 3,
    md: {
        justifyContent: "center",
        flexDirection: "row"
    }
});

const summaryBoxStyle = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    pt: 2,
    pr: 2,
    pb: 4,
    pl: 2,
    gap: 2,
    backgroundColor: "#fff",
    borderRadius: 2,
    maxWidth: "336px",
    width: "100%"
});

const contentStyle = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 1,
    flex: "1 0 80px"
});

const brandIconWrap = css({
    width: "124px",
    height: "124px"
});

const buttonWrap = css({
    pt: 0,
    pr: 0,
    pb: 0,
    pl: 0
});

const button = css({
    width: "200px",
    height: "40px",
    mb: 0
});
import {css} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {FiltersIcon} from "@pg-design/icons-module";
import {BrandPreferencesIcon} from "@pg-design/icons-module";
import {Image} from "@pg-design/image-module";
import {Text} from "@pg-design/text-module";
import {kmAppLink} from "@pg-mono/km-routes";

import {useResponsiveLinkTarget} from "../../../hooks/use_responsive_link_target";
import {KitchenType} from "../../../property_configurator/types/KitchenType";
import {useAppSelector} from "../../../store/hooks";
import {useIsAuthenticated} from "../../../user/hooks/use_is_authenticated";
import {getFullKMLink} from "../../../utils/get_full_km_link";
import {useForYouModalClose} from "../../hooks/use_for_you_modal_close";
import {ForYouAdditionalAreaType} from "../../types/IForYouAdditional";
import {IForYouModal} from "../../types/IForYouModal";
import {ForYouModalButton} from "./ForYouModalButton";

const consultantImg = require("../../images/for-you-consultant.png");

const kmPreferencesLink = getFullKMLink(kmAppLink.site.user.preferencesWithParams);

export const ForYouModalSummary = () => {
    const isAuthenticated = useIsAuthenticated();
    const modalState = useAppSelector((state) => state.forYouModal);

    const closeModal = useForYouModalClose();
    const target = useResponsiveLinkTarget();

    return (
        <div className={summaryContent}>
            {isAuthenticated && (
                <SummaryBox
                    image={
                        <div className={brandIconWrap}>
                            <BrandPreferencesIcon wrapperSize="12.4" wrapperColor="transparent" size="12.4" />
                        </div>
                    }
                    title="Twoje kryteria"
                    text={convertToSummaryText(modalState)}
                    button={
                        <Button iconLeft={FiltersIcon} href={kmPreferencesLink} className={button} variant="filled_primary" target={target}>
                            Zarządzaj
                        </Button>
                    }
                />
            )}
            <SummaryBox
                image={<Image src={consultantImg} width="124px" height="124px" alt="zdjęcie konsutanta" />}
                title="Anna Kowalska"
                text={
                    <>
                        Twój doradca nieruchomości
                        <br />
                        Godziny pracy: <br />
                        pon-pt: 9:00-18:00
                    </>
                }
                button={<ForYouModalButton onClick={closeModal} buttonText="Zakończ" className={buttonWrap} buttonClassName={button} />}
            />
        </div>
    );
};

function convertToSummaryText(modalState: IForYouModal) {
    const hasRooms = Boolean(modalState.criteria?.rooms.lower);

    const areaText = `${!hasRooms ? "Mieszkanie " : ""}${modalState.criteria?.area.lower}-${modalState.criteria?.area.upper} m2`;
    const budgetText = `do ${modalState.criteria?.budget} zł`;
    const locationText = `w lokalizacji ${modalState.mainRegionName}`;

    const textParts = [areaText, budgetText, locationText];

    if (hasRooms) {
        const singleRoom = modalState.criteria?.rooms.lower === modalState.criteria?.rooms.upper;
        const roomsText = singleRoom ? modalState.criteria?.rooms.lower : `${modalState.criteria?.rooms.lower}-${modalState.criteria?.rooms.upper}`;

        const roomText = `Mieszkanie ${roomsText} pokojowe`;
        textParts.splice(0, 0, roomText);
    }

    const additionalMap = new Map<ForYouAdditionalAreaType, string>([
        [ForYouAdditionalAreaType.BALCONY, "balkonem"],
        [ForYouAdditionalAreaType.LOGGIA, "loggią"],
        [ForYouAdditionalAreaType.TERRACE, "tarasem"],
        [ForYouAdditionalAreaType.GARDEN, "ogródkiem"]
    ]);

    const kitchenMap = new Map<KitchenType, string>([
        [KitchenType.KITCHENETTE, "aneksem kuchennym"],
        [KitchenType.CLOSED, "oddzielną kuchnią"]
    ]);

    const hasAdditionalAndKitchent = Boolean(modalState.additional?.additional_areas && modalState.additional?.kitchen);
    if (hasAdditionalAndKitchent) {
        const additionalText = additionalMap.get(modalState.additional?.additional_areas as unknown as ForYouAdditionalAreaType);
        const kitchenText = kitchenMap.get(modalState.additional?.kitchen as KitchenType);
        const additionalAndKitchenText = `z ${kitchenText} i ${additionalText}`;

        textParts.splice(textParts.length - 1, 0, additionalAndKitchenText);
    }

    const hasAdditionalButNoKitchen = Boolean(modalState.additional?.additional_areas && !modalState.additional?.kitchen);
    if (hasAdditionalButNoKitchen) {
        const additionalText = additionalMap.get(modalState.additional?.additional_areas as unknown as ForYouAdditionalAreaType);
        const additionalTextFull = `z ${additionalText}`;
        textParts.splice(textParts.length - 1, 0, additionalTextFull);
    }

    const hasKitchenButNoAdditional = Boolean(modalState.additional?.kitchen && !modalState.additional?.additional_areas);
    if (hasKitchenButNoAdditional) {
        const kitchenText = kitchenMap.get(modalState.additional?.kitchen as KitchenType);
        const kitchenTextFull = `z ${kitchenText}`;
        textParts.splice(textParts.length - 1, 0, kitchenTextFull);
    }

    return textParts.join(", ");
}
interface SummaryBoxProps {
    image: React.ReactNode;
    title: string;
    text: string | React.ReactNode;
    button: React.ReactNode;
}

function SummaryBox(props: SummaryBoxProps) {
    const {image, title, text, button} = props;

    return (
        <div className={summaryBoxStyle}>
            {image}
            <div className={contentStyle}>
                <Text variant="headline_5" as="p" align="center">
                    {title}
                </Text>
                <Text variant="info_txt_1" align="center" color="var(--colors-gray-700)">
                    {text}
                </Text>
            </div>
            {button}
        </div>
    );
}

const summaryContent = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    backgroundColor: "var(--colors-gray-100)",

    pt: 1.5,
    pr: 1.5,
    pb: 1.5,
    pl: 1.5,
    width: "100%",
    height: "100%",
    gap: 3,
    md: {
        justifyContent: "center",
        flexDirection: "row"
    }
});

const summaryBoxStyle = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    pt: 2,
    pr: 2,
    pb: 4,
    pl: 2,
    gap: 2,
    backgroundColor: "#fff",
    borderRadius: 2,
    maxWidth: "336px",
    width: "100%"
});

const contentStyle = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 1,
    flex: "1 0 80px"
});

const brandIconWrap = css({
    width: "124px",
    height: "124px"
});

const buttonWrap = css({
    pt: 0,
    pr: 0,
    pb: 0,
    pl: 0
});

const button = css({
    width: "200px",
    height: "40px",
    mb: 0
});
